/**
 * Audit Page
 * View audit logs for all system configurations (dynamically discovered)
 */

import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Loading } from '@/components/common';
import { AuditLogViewer, useAuditTypes } from '@/features/system-settings';
import { useI18n } from '@/i18n';
import {
  Server,
  Brain,
  MessageSquare,
  Package,
  Wrench,
  Layers,
  FileText,
  FileCode,
  Flag,
  Settings,
  HelpCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Icon mapping for known config types
const ICON_MAP: Record<string, LucideIcon> = {
  mcp_servers: Server,
  mem0_settings: Brain,
  first_message_instruction: MessageSquare,
  artifact_types: Package,
  tool_enhancement: Wrench,
  tool_combo_templates: Layers,
  system_prompt_templates: FileText,
  output_schemas: FileCode,
  feature_flags: Flag,
};

// Display name mapping for known config types - now moved to i18n

// Format config_type for display (fallback for unknown types)
function formatConfigType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AuditPage() {
  const { messages, language } = useI18n();
  const t = messages.systemSettings;

  const { data: auditTypesData, isLoading, error } = useAuditTypes();
  const [activeType, setActiveType] = useState<string>('');

  // Set first type as active when data loads
  useEffect(() => {
    if (auditTypesData?.types?.length && !activeType) {
      setActiveType(auditTypesData.types[0]);
    }
  }, [auditTypesData, activeType]);

  const getIcon = (type: string): LucideIcon => {
    return ICON_MAP[type] || Settings;
  };

  const getDisplayName = (type: string): string => {
    // Use i18n translations for known config types
    const configTypeKey = type as keyof typeof t.audit.configTypeNames;
    if (t.audit.configTypeNames[configTypeKey]) {
      return t.audit.configTypeNames[configTypeKey];
    }
    // Fallback to formatted type name for unknown types
    return formatConfigType(type);
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Loading message={t.audit.pageTitle} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-red-600">Failed to load audit types: {error.message}</div>
      </div>
    );
  }

  const auditTypes = auditTypesData?.types || [];

  if (auditTypes.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t.audit.pageTitle}</h1>
          <p className="text-gray-600 mt-1">{t.audit.pageDescription}</p>
        </div>
        <Card className="p-8 text-center">
          <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No audit records found</p>
          <p className="text-sm text-gray-500 mt-1">
            Audit logs will appear here after configuration changes are made.
          </p>
        </Card>
      </div>
    );
  }

  const ActiveIcon = getIcon(activeType);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t.audit.pageTitle}</h1>
        <p className="text-gray-600 mt-1">
          {t.audit.pageDescription}
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar - Category selection */}
        <Card className="w-64 p-4 h-fit flex-shrink-0">
          <h3 className="text-sm font-medium text-gray-500 mb-3">
            {t.audit.viewLogsFor}
          </h3>
          <nav className="space-y-1">
            {auditTypes.map((type) => {
              const Icon = getIcon(type);
              const isActive = activeType === type;
              return (
                <button
                  key={type}
                  onClick={() => setActiveType(type)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{getDisplayName(type)}</span>
                </button>
              );
            })}
          </nav>
        </Card>

        {/* Right content - Audit logs */}
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ActiveIcon className="w-5 h-5" />
            {getDisplayName(activeType)} {language === 'zh' ? '审计日志' : 'Audit Log'}
          </h2>
          {activeType && <AuditLogViewer type={activeType} />}
        </div>
      </div>
    </div>
  );
}
